import type { DisciplinaryCase } from "../../types";
import { roundKeyId } from "../competition/roundBuckets";

/**
 * Match cases of a PV grouped exactly like the PDF renderer groups them
 * (pv-renderer pv-pdf-fetcher.ts §9): one bucket per (league group, game
 * day) — per (league group, round) for a cup match (isCupMatch), whose
 * game_day is almost always 1; a cup match without a round is its own
 * bucket —, buckets in order of first appearance in builder order, cases
 * sorted by case number inside a bucket. The builder renders these buckets so
 * what the league sees is what gets printed. Same buckets as the SQL RPCs
 * renumber_pv_match_cases / reorder_pv_match_cases (migration
 * 20261019101400_pv_buckets_cup_rounds.sql).
 */
export interface MatchBucket {
    key: string;
    leagueGroupId: string | null;
    /** game_day of a championship bucket; null for a cup bucket. */
    round: number | null;
    /** Cup bucket: the round (cup_rounds.code, null = round unknown). */
    isCup: boolean;
    cupRound: string | null;
    cupRoundNameAr: string | null;
    leagueGroupNameAr: string | null;
    cases: DisciplinaryCase[];
}

/** Subset of MatchCaseMeta the grouping needs. */
export interface MatchBucketMeta {
    leagueGroupId?: string | null;
    leagueGroupNameAr?: string | null;
    round?: number | null;
    /** Cup match (isCupMatch): bucketed by its round, never by its game_day. */
    isCup?: boolean | null;
    cupRound?: string | null;
    cupRoundNameAr?: string | null;
}

/** Builder order, as the renderer reads cases: display_order (nulls last), created_at. */
const builderOrder = (a: DisciplinaryCase, b: DisciplinaryCase): number => {
    const ao = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
    const bo = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    const ac = a.createdAt ?? "";
    const bc = b.createdAt ?? "";
    if (ac !== bc) return ac < bc ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
};

/**
 * The renderer sorts with parseInt(case_number ?? '0'): a missing number
 * counts as 0 and prints first. Unparseable numbers go last here (the
 * renderer's NaN comparison is unspecified).
 */
const printedNumber = (c: DisciplinaryCase): number => {
    const n = parseInt(c.caseNumber ?? "0", 10);
    return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
};

/** Championship: « group::4 » (unchanged); cup: « group::cup:semi_final » / « group::cup:none ». */
export const matchBucketKey = (
    leagueGroupId: string | null,
    round: number | null,
    cup?: { round: string | null } | null,
): string =>
    `${leagueGroupId ?? "ungrouped"}::${cup ? roundKeyId({ kind: "cup", round: cup.round }) : round ?? ""}`;

export function bucketMatchCases(
    cases: DisciplinaryCase[],
    meta: Record<string, MatchBucketMeta | undefined>,
): MatchBucket[] {
    const buckets = new Map<string, MatchBucket>();
    for (const c of [...cases].sort(builderOrder)) {
        const m = meta[c.id];
        const leagueGroupId = m?.leagueGroupId ?? c.leagueGroupId ?? null;
        const isCup = !!m?.isCup;
        const cupRound = isCup ? m?.cupRound || null : null;
        const round = !isCup && typeof m?.round === "number" ? m.round : null;
        const key = matchBucketKey(leagueGroupId, round, isCup ? { round: cupRound } : null);
        let bucket = buckets.get(key);
        if (!bucket) {
            bucket = {
                key, leagueGroupId, round, isCup, cupRound,
                cupRoundNameAr: isCup ? m?.cupRoundNameAr ?? null : null,
                leagueGroupNameAr: m?.leagueGroupNameAr ?? null, cases: [],
            };
            buckets.set(key, bucket);
        }
        if (!bucket.leagueGroupNameAr && m?.leagueGroupNameAr) bucket.leagueGroupNameAr = m.leagueGroupNameAr;
        if (bucket.isCup && !bucket.cupRoundNameAr && m?.cupRoundNameAr) bucket.cupRoundNameAr = m.cupRoundNameAr;
        bucket.cases.push(c);
    }
    const out = Array.from(buckets.values());
    // Array.prototype.sort is stable: equal numbers keep builder order.
    for (const b of out) b.cases.sort((x, y) => printedNumber(x) - printedNumber(y));
    return out;
}

export const flattenBuckets = (buckets: MatchBucket[]): string[] =>
    buckets.flatMap((b) => b.cases.map((c) => c.id));

/** Case ids after moving a whole bucket onto another's position; null if nothing moves. */
export function moveBucket(buckets: MatchBucket[], fromKey: string, toKey: string): string[] | null {
    const from = buckets.findIndex((b) => b.key === fromKey);
    const to = buckets.findIndex((b) => b.key === toKey);
    if (from < 0 || to < 0 || from === to) return null;
    const next = [...buckets];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return flattenBuckets(next);
}

/**
 * Case ids after moving a card onto another card's position inside the same
 * bucket. Null across buckets: a card's bucket comes from its match, not
 * from where it is dropped.
 */
export function moveCaseInBucket(buckets: MatchBucket[], activeId: string, overId: string): string[] | null {
    const bucket = buckets.find((b) => b.cases.some((c) => c.id === activeId));
    if (!bucket || !bucket.cases.some((c) => c.id === overId)) return null;
    const from = bucket.cases.findIndex((c) => c.id === activeId);
    const to = bucket.cases.findIndex((c) => c.id === overId);
    if (from === to) return null;
    const cases = [...bucket.cases];
    const [moved] = cases.splice(from, 1);
    cases.splice(to, 0, moved);
    return flattenBuckets(buckets.map((b) => (b.key === bucket.key ? { ...b, cases } : b)));
}

/**
 * First number of a renumbering: the smallest plain numeric case number
 * already used, so a PV continuing a previous one (starting at 40) keeps its
 * offset. 1 when none is numbered.
 */
export function smallestCaseNumber(numbers: Array<string | null | undefined>): number {
    const nums = numbers
        .map((n) => (n && /^\d+$/.test(n.trim()) ? Number(n.trim()) : NaN))
        .filter((n) => Number.isInteger(n) && n >= 1);
    return nums.length > 0 ? Math.min(...nums) : 1;
}

/** "#3" or "#3–#6" for a bucket header; null when no case is numbered. */
export function bucketNumberRange(bucket: MatchBucket): string | null {
    const nums = bucket.cases
        .map((c) => c.caseNumber?.trim())
        .filter((n): n is string => !!n);
    if (nums.length === 0) return null;
    const first = nums[0];
    const last = nums[nums.length - 1];
    return first === last ? `#${first}` : `#${first}–#${last}`;
}
